import type { AggregateRow } from '@airmail/engine';
import { cx } from '@/ui';
import { useT } from '@/i18n';

/**
 * Wertungstabelle im Kundenformat (docs/13 §3): Rang · Name · Punkte · S · N ·
 * Diff Spiele · Plus · Minus · Diff Punkte. Für Mannschaft, Paar und Spieler.
 */
export function AggregateTable({
  rows,
  nameLabel,
  extraHead,
  extraCell,
  highlightTop = 0,
  rowClass,
}: {
  rows: AggregateRow[];
  nameLabel?: string;
  extraHead?: React.ReactNode;
  extraCell?: (r: AggregateRow) => React.ReactNode;
  highlightTop?: number;
  rowClass?: (r: AggregateRow) => string | undefined;
}) {
  const t = useT();
  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-line">
          <Th className="w-8">{t('Rang')}</Th>
          <Th>{nameLabel ?? t('Name')}</Th>
          <Th right>{t('Pkt')}</Th>
          <Th right>{t('Sp')}</Th>
          <Th right>{t('S')}</Th>
          <Th right>{t('N')}</Th>
          <Th right title={t('Siege minus Niederlagen')}>
            {t('±Sp')}
          </Th>
          <Th right>{t('+')}</Th>
          <Th right>{t('−')}</Th>
          <Th right>{t('Diff')}</Th>
          {extraHead}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.key} className={cx('border-b border-line2', i < highlightTop && r.rank > 0 && 'border-l-2 border-l-live', rowClass?.(r))}>
            {/* Rang 0 = außer Wertung (Saison, docs/13 §4) */}
            <td className="px-2 py-1.5 font-mono text-[11px] text-dim">{r.rank > 0 ? r.rank : '–'}</td>
            <td className="px-2 py-1.5 font-bold tracking-tight">{r.name}</td>
            <td className="px-2 py-1.5 text-right font-mono font-bold">{r.points}</td>
            <td className="px-2 py-1.5 text-right font-mono">{r.played}</td>
            <td className="px-2 py-1.5 text-right font-mono">{r.wins}</td>
            <td className="px-2 py-1.5 text-right font-mono">{r.losses}</td>
            <td className="px-2 py-1.5 text-right font-mono">{signed(r.wins - r.losses)}</td>
            <td className="px-2 py-1.5 text-right font-mono">{r.scoreFor}</td>
            <td className="px-2 py-1.5 text-right font-mono">{r.scoreAgainst}</td>
            <td className="px-2 py-1.5 text-right font-mono">{signed(r.scoreDiff)}</td>
            {extraCell?.(r)}
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={10} className="px-2 py-4 text-center text-dim">
              {t('Noch keine Ergebnisse.')}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function Th({ children, right, className, title }: { children: React.ReactNode; right?: boolean; className?: string; title?: string }) {
  return (
    <th title={title} className={cx('label font-normal px-2 py-1.5', right ? 'text-right' : 'text-left', className)}>
      {children}
    </th>
  );
}

export function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}
